import React, { PureComponent, type ReactNode } from 'react';

import type { PodAudioPlayerAppState } from '../../ducks/pod_audio_player/types';
import Icon, { ICONS } from '../../icons/icon';

import styles from './pod_audio_player.module.css';

type Props = {
  assetId: string;
  audioPlayerStore: PodAudioPlayerAppState;
  onLoadAudioUrlClick: () => void;
};

class PodAudioPlayer extends PureComponent<Props> {
  state = {
    isPlaying: false,
  };

  audio = React.createRef<HTMLAudioElement>();

  playOnUpdate = false;

  playPromise: Promise<unknown> | null = null;

  componentDidUpdate(prevProps: Readonly<Props>): void {
    const { audioPlayerStore, assetId } = this.props;
    const audio = this.audio.current;
    if (!audio) {
      return;
    }

    if (audioPlayerStore && prevProps.assetId !== audioPlayerStore.assetId) {
      this.setState({
        isPlaying: false,
      });
      audio.pause();
    }

    let audioProxyUrl: string | undefined = '';
    if (audioPlayerStore && audioPlayerStore.audioProxies) {
      if (audioPlayerStore.audioProxies[assetId]) {
        audioProxyUrl = audioPlayerStore.audioProxies[assetId].url ?? undefined;
      }

      if (
        !this.state.isPlaying &&
        audioPlayerStore.assetId === assetId &&
        audioProxyUrl
      ) {
        audio.src = audioProxyUrl;

        if (this.playOnUpdate) {
          this.playOnUpdate = false;
          this.playPromise = audio.play();
        }
      }
    }
  }

  requestToLoadUrl = (): void => {
    this.playOnUpdate = true;
    this.props.onLoadAudioUrlClick();
  };

  handlePlay = (): void => {
    this.setState({
      isPlaying: true,
    });
  };

  handlePause = (): void => {
    this.setState({
      isPlaying: false,
    });
  };

  playPauseAudio = (): void => {
    const { isPlaying } = this.state;
    const audio = this.audio.current;
    if (!audio) {
      return;
    }

    if (!isPlaying) {
      this.playPromise = audio.play().catch((e) => {
        throw new Error(`Failed to play pod audio preview ${e}`);
      });
    } else if (isPlaying) {
      if (this.playPromise) {
        this.playPromise.then(() => {
          audio.pause();
          this.playPromise = null;
        });
      } else {
        audio.pause();
      }
    }
  };

  render(): ReactNode {
    const { assetId, audioPlayerStore } = this.props;
    const { isPlaying } = this.state;

    let audioProxyUrl: string | undefined = '';
    if (audioPlayerStore && audioPlayerStore.audioProxies[assetId]) {
      audioProxyUrl = audioPlayerStore.audioProxies[assetId].url ?? undefined;
    }

    const isLoaded = audioPlayerStore
      ? audioPlayerStore.assetId === assetId && audioProxyUrl
      : undefined;

    let icon = <Icon icon={ICONS.Play} />;
    if (isPlaying) {
      icon = <Icon icon={ICONS.Pause} className={styles.pause_icon} />;
    }

    if (
      audioPlayerStore &&
      audioPlayerStore.assetId === assetId &&
      !audioProxyUrl
    ) {
      icon = <div className={styles.loading} />;
    }

    let onClick = this.requestToLoadUrl;
    if (isLoaded) {
      onClick = this.playPauseAudio;
    }

    if (
      audioPlayerStore &&
      audioPlayerStore.assetId === assetId &&
      !audioProxyUrl
    ) {
      onClick = () => {};
    }

    return (
      <div className={styles.component}>
        <button
          onClick={onClick}
          onMouseDown={(e) => {
            e.stopPropagation();
          }}
          className={styles.play_pause_button}
          type="button"
        >
          {icon}
        </button>
        {isPlaying ? (
          <div className={styles.speaker}>
            <Icon icon={ICONS.Wave} className={styles.wave} />
            <Icon icon={ICONS.Wave} className={styles.wave} />
            <Icon icon={ICONS.Wave} className={styles.wave} />
          </div>
        ) : null}
        <audio
          ref={this.audio}
          onPlay={this.handlePlay}
          onPause={this.handlePause}
        />
      </div>
    );
  }
}

export default PodAudioPlayer;
